/**
 * A simple, user-facing importance label — Low / Medium / High — layered on top of the existing
 * relative 1-100 competency weight (see `VerticalCompetency.weight`, verticals.ts). The tier is
 * NEVER stored: it's derived from the numbers already in play, and a tier pick is converted back
 * into numbers immediately, so the weight stays the single source of truth for scoring. Used by
 * both the admin vertical editor (a vertical's own competency set) and the per-requisition weight
 * override — same functions, different callers, each passing only the numbers it owns.
 */
const TIER_UNITS = { low: 1, medium: 2, high: 3 };
/**
 * Which tier a weight currently represents, relative to its siblings in the same set (a
 * vertical's competencies, or one requisition's override map). Weight is RELATIVE, not absolute
 * (the scorer normalises it — see `normalisedWeights` in verticals.ts), so a tier has to be
 * defined the same way: against an even split across the set, not a fixed number.
 *
 * A single-competency set has nothing to be relative to — it IS the whole composite — so it
 * always reads as "high".
 */
export function deriveTier(weight, siblingWeights) {
    if (siblingWeights.length < 2)
        return "high";
    const total = siblingWeights.reduce((a, b) => a + b, 0);
    if (total <= 0)
        return "medium";
    const evenShare = 100 / siblingWeights.length;
    const share = (weight / total) * 100;
    if (share >= evenShare * 1.3)
        return "high";
    if (share <= evenShare * 0.7)
        return "low";
    return "medium";
}
/**
 * Recompute every weight in a set after ONE competency's tier is changed. Every OTHER competency
 * keeps whichever tier it currently displays (seeded via `deriveTier`), so picking a tier for one
 * competency proportionally rebalances the others rather than resetting them — the same kind of
 * move "Even them out" already makes today, just non-uniform. The result always sums to exactly
 * 100 (largest-remainder rounding), so an admin using tiers never has to reach for "Even them
 * out" afterward to satisfy the vertical editor's save gate.
 */
export function applyTier(weights, changedIndex, newTier) {
    const tiers = weights.map((w, i) => (i === changedIndex ? newTier : deriveTier(w, weights)));
    const units = tiers.map((t) => TIER_UNITS[t]);
    const totalUnits = units.reduce((a, b) => a + b, 0);
    const raw = units.map((u) => (100 * u) / totalUnits);
    return distributeToExactTotal(raw, 100);
}
/** Round every value down, then hand the leftover whole numbers to the largest fractional
 *  remainders — so a set of numbers that should sum to `target` actually does. */
function distributeToExactTotal(raw, target) {
    const floors = raw.map(Math.floor);
    const remainder = target - floors.reduce((a, b) => a + b, 0);
    const order = raw
        .map((r, i) => ({ i, frac: r - Math.floor(r) }))
        .sort((a, b) => b.frac - a.frac);
    const result = [...floors];
    for (let k = 0; k < remainder && k < order.length; k++) {
        const entry = order[k];
        if (!entry)
            continue;
        result[entry.i] = (result[entry.i] ?? 0) + 1;
    }
    return result;
}
//# sourceMappingURL=competencyImportance.js.map